import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import CollaboratorsDataTable from '@/pages/_partials/CollaboratorsDataTable';
import {
    Building2,
    Pencil,
    Trash2,
    ClipboardList,
    Filter,
    Camera,
    Users,
    Coins,
    Search,
    X,
    FileText,
    TrendingUp,
    UserCheck,
    EllipsisVertical,
    Plus,
    Loader2,
    User,
} from 'lucide-react';
import { BackButton } from '@/components/ui/app/back-button';
import SheetSubsidiaryEdit from './_partials/sheets/SheetSubsidiaryEdit';
import SheetSubsidiaryInvite from './_partials/sheets/SheetSubsidiaryInvite';
import SheetCollaboratorEdit from './_partials/sheets/SheetCollaboratorEdit';
import SheetOwnerEdit from './_partials/sheets/SheetOwnerEdit';
import SheetActivityFilter from './_partials/sheets/SheetActivityFilter';
import SheetSubsidiaryRights from './_partials/sheets/SheetSubsidiaryRights';
import AlertDialogDeleteCollaborator from './_partials/dialogs/AlertDialogDeleteCollaborator';
import AlertDialogDeleteSubsidiary from './_partials/dialogs/AlertDialogDeleteSubsidiary';
import SheetSubsidiaryCreate from '@/pages/account/index/_partials/sheets/SheetSubsidiaryCreate';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { getRoleLabel, getRoleBadgeClass, getUserStatusLabel, getUserStatusBadgeClass } from '@/lib/utils/utils';
import { toast } from 'sonner';
import { useElementHeight } from '@/lib/hooks/use-element-height';
import { useBalance } from '@/lib/contexts/balance-context';
import { useAuth } from '@/lib/contexts/auth-context';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { MobileHeaderNavigation } from '@/pages/_partials/mobile-headers';
import { useTranslation } from 'react-i18next';

export default function SubsidiaryPage() {
    const { t } = useTranslation();
    const { subsidiaryId } = useParams();
    const navigate = useNavigate();
    const { balance: parentBalance, refreshBalance } = useBalance();
    const { user: authUser, isOwnerOrAdmin } = useAuth();
    const { canOnDescendants, hasEditablePermissions } = usePermissions();
    const companyId = authUser?.company_uniq_id ?? authUser?.company?.uniq_id ?? null;

    const companyInfoRef = useRef(null);

    // Sheet states
    const [editOpen, setEditOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editCollaboratorOpen, setEditCollaboratorOpen] = useState(false);
    const [selectedCollaborator, setSelectedCollaborator] = useState(null);
    const [deleteCollaboratorOpen, setDeleteCollaboratorOpen] = useState(false);
    const [collaboratorToDelete, setCollaboratorToDelete] = useState(null);
    const [activityFilterOpen, setActivityFilterOpen] = useState(false);
    const [activityFilter, setActivityFilter] = useState('all');
    const [activityFilterResetKey, setActivityFilterResetKey] = useState(0);
    const [rightsOpen, setRightsOpen] = useState(false);
    const [deleteSubsidiaryOpen, setDeleteSubsidiaryOpen] = useState(false);
    const [subsidiaryCreateOpen, setSubsidiaryCreateOpen] = useState(false);
    const [isSubmittingSheet, setIsSubmittingSheet] = useState(false);
    const [ownerEditOpen, setOwnerEditOpen] = useState(false);

    // Data states
    const [subsidiary, setSubsidiary] = useState(null);

    // URL de retour : parent subsidiary ou page company
    const backUrl =
        subsidiary?.parent_company_uniq_id && subsidiary.parent_company_uniq_id !== companyId
            ? `/settings/subsidiary/${subsidiary.parent_company_uniq_id}`
            : '/settings/company';

    const [subsidiaryLogoUrl, setSubsidiaryLogoUrl] = useState('');
    const [collaborators, setCollaborators] = useState([]);
    const [collaboratorSearch, setCollaboratorSearch] = useState('');
    const [collaboratorsPage, setCollaboratorsPage] = useState(1);
    const [collaboratorsPageSize, setCollaboratorsPageSize] = useState(10);
    const [collaboratorsPageCount, setCollaboratorsPageCount] = useState(1);
    const [collaboratorsTotal, setCollaboratorsTotal] = useState(0);
    const [collaboratorsSearchApi, setCollaboratorsSearchApi] = useState('');
    const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);
    const [activities, setActivities] = useState([]);
    const [subSubsidiaries, setSubSubsidiaries] = useState([]);
    const [isLoadingSubsidiaries, setIsLoadingSubsidiaries] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Token transfer
    const [tokenAmount, setTokenAmount] = useState('');
    const [isSendingTokens, setIsSendingTokens] = useState(false);

    // Menu dropdown
    const [menuOpen, setMenuOpen] = useState(false);

    const collaboratorsHeight = useElementHeight(companyInfoRef, [subsidiary]);

    // Dérivés selon le type de sous-compte.
    // Détection robuste : on regarde à la fois le flag company.is_fake (créé automatiquement
    // à l'inscription d'un particulier) ET le rôle propriétaire 'user' (cas legacy en BDD
    // où la fake company n'a pas été créée). Note : le modèle User sérialise `role` comme une string.
    const isIndividual = subsidiary?.is_fake === true || subsidiary?.owner?.role === 'user';
    const ownerFullName = [subsidiary?.owner?.first_name, subsidiary?.owner?.last_name].filter(Boolean).join(' ');

    // Colonnes pour la DataTable des sous-comptes
    const isSuperAdmin = authUser?.role === 'admin';

    const subsidiaryColumns = useMemo(
        () => [
            {
                accessorKey: 'name',
                header: t('subsidiary:nom2'),
                cell: ({ row }) => <span className="text-[13px] text-foreground">{row.original.name}</span>,
                meta: { hideOnMobile: true },
            },
            ...(isSuperAdmin
                ? [
                      {
                          accessorKey: 'type',
                          header: t('subsidiary:type'),
                          cell: ({ row }) => (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs text-muted-foreground">
                                  {row.original.is_fake === true || row.original.owner?.role === 'user'
                                      ? t('subsidiary:particulier')
                                      : t('subsidiary:entreprise')}
                              </span>
                          ),
                          meta: { hideOnMobile: true },
                      },
                  ]
                : []),
            {
                accessorKey: 'email',
                header: t('subsidiary:emailDeContact'),
                cell: ({ row }) => <span className="text-[13px] text-muted-foreground">{row.original.email}</span>,
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'balance',
                header: t('subsidiary:solde'),
                cell: ({ row }) => (
                    <div className="flex items-center gap-1.5">
                        <Coins className="h-4 w-4 text-brand" />
                        <span className="text-[13px] text-foreground font-medium">{row.original.balance ?? 0}</span>
                    </div>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'phone',
                header: t('subsidiary:telephone'),
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">
                        {row.original.phone ?? row.original.phone_number ?? '—'}
                    </span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'siret',
                header: 'SIRET',
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">{row.original.siret ?? '—'}</span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                id: 'address',
                header: t('subsidiary:adresse'),
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">
                        {[row.original.street, row.original.cp, row.original.city].filter(Boolean).join(', ') || '—'}
                    </span>
                ),
                meta: { hideOnMobile: true },
            },
        ],
        [isSuperAdmin, t],
    );

    // Colonnes pour la DataTable de l'historique d'actions
    const activityColumns = useMemo(
        () => [
            {
                accessorKey: 'type',
                header: t('subsidiary:type'),
                cell: ({ row }) => {
                    const type = row.original.type;
                    const iconClass = 'h-5 w-5';
                    const bgClass = `w-10 h-10 rounded-full flex items-center justify-center ${
                        type === 'procedure_filled'
                            ? 'bg-badge-filled/20'
                            : type === 'procedure_created'
                              ? 'bg-badge-created/20'
                              : type === 'photo'
                                ? 'bg-secondary/20'
                                : type === 'tokens'
                                  ? 'bg-secondary/20'
                                  : type === 'invitation'
                                    ? 'bg-badge-invitation/20'
                                    : 'bg-muted'
                    }`;
                    return (
                        <div className={bgClass}>
                            {type === 'procedure_filled' && <FileText className={`${iconClass} text-badge-filled`} />}
                            {type === 'procedure_created' && (
                                <ClipboardList className={`${iconClass} text-badge-created`} />
                            )}
                            {type === 'photo' && <Camera className={`${iconClass} text-secondary`} />}
                            {type === 'tokens' && <TrendingUp className={`${iconClass} text-secondary`} />}
                            {type === 'invitation' && <UserCheck className={`${iconClass} text-badge-invitation`} />}
                            {!type && <ClipboardList className={`${iconClass} text-muted-foreground`} />}
                        </div>
                    );
                },
                meta: { className: 'w-[60px]', hideOnMobile: true },
            },
            {
                accessorKey: 'title',
                header: t('subsidiary:description'),
                cell: ({ row }) => <span className="text-[13px] text-foreground">{row.original.title}</span>,
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'date',
                header: t('subsidiary:date'),
                cell: ({ row }) => <span className="text-[13px] text-muted-foreground">{row.original.date}</span>,
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'collaborator',
                header: t('subsidiary:responsable'),
                cell: ({ row }) => (
                    <span className="text-[13px] text-muted-foreground">{row.original.collaborator ?? '-'}</span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'delta',
                header: () => <span className="text-right block">{t('subsidiary:montant')}</span>,
                cell: ({ row }) => {
                    const delta = row.original.delta;
                    if (delta === 0 || delta === undefined) {
                        return <span className="text-[14px] text-muted-foreground block text-right">-</span>;
                    }
                    return (
                        <span
                            className={`text-[15px] font-medium block text-right ${
                                delta > 0 ? 'text-secondary' : 'text-destructive'
                            }`}
                        >
                            {delta > 0 ? `+${delta}` : delta}
                        </span>
                    );
                },
                meta: { hideOnMobile: true, className: 'text-right' },
            },
        ],
        [t],
    );

    const formatDate = (value) => {
        if (!value) return '—';
        try {
            return new Date(value).toLocaleDateString('fr-FR');
        } catch {
            return '—';
        }
    };

    // Mapper le type de transaction vers un type lisible pour l'UI
    const usageToType = (usage) => {
        if (!usage) return 'tokens';
        const u = usage.toLowerCase();
        if (u.includes('photo') || u.includes('picture')) return 'photo';
        if (u.includes('procedure') || u.includes('intervention') || u.includes('rapport')) return 'procedure_filled';
        if (u.includes('dossier') || u.includes('folder')) return 'procedure_created';
        if (u.includes('achat') || u.includes('credit') || u.includes('purchase')) return 'tokens';
        if (u.includes('invite') || u.includes('user')) return 'invitation';
        return 'tokens';
    };

    // Fetch collaborators with server-side pagination
    const refreshCollaborators = useCallback(async () => {
        if (!subsidiaryId || !companyId) return;
        setIsLoadingCollaborators(true);
        try {
            const res = await horodatyApi.subsidiaries.getUsers(companyId, subsidiaryId, {
                paginate: true,
                page: collaboratorsPage,
                per_page: collaboratorsPageSize,
                ...(collaboratorsSearchApi ? { search: collaboratorsSearchApi } : {}),
            });
            const list = res?.data?.data ?? [];
            const meta = res?.data?.meta;
            const mapped = (Array.isArray(list) ? list : [])
                .filter((u) => u?.status !== 'Inactif' && u?.status !== 'INACTIVE')
                .map((u) => ({
                    id: u.uniq_id ?? u.id,
                    name: u.name ?? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim(),
                    email: u.email,
                    role: u.role ?? null,
                    status: u.status ?? null,
                    raw: u,
                }));
            setCollaborators(mapped);
            if (meta) {
                setCollaboratorsPageCount(meta.last_page ?? 1);
                setCollaboratorsTotal(meta.total ?? 0);
            }
        } catch {
            // silent
        } finally {
            setIsLoadingCollaborators(false);
        }
    }, [subsidiaryId, companyId, collaboratorsPage, collaboratorsPageSize, collaboratorsSearchApi]);

    const collaboratorSearchTimeout = useRef(null);
    const handleCollaboratorSearchChange = useCallback((value) => {
        setCollaboratorSearch(value);
        if (collaboratorSearchTimeout.current) clearTimeout(collaboratorSearchTimeout.current);
        collaboratorSearchTimeout.current = setTimeout(() => {
            setCollaboratorsSearchApi(value);
            setCollaboratorsPage(1);
        }, 400);
    }, []);

    // Fetch subsidiary data
    const fetchSubsidiary = useCallback(async () => {
        if (!subsidiaryId || !companyId) return;
        setIsLoading(true);
        try {
            // 1) Charger d'abord les infos du sous-compte pour connaître son type (is_fake)
            const subRes = await horodatyApi.subsidiaries.get(companyId, subsidiaryId);
            const subData = subRes?.data?.data ?? subRes?.data ?? null;
            const isFake = subData?.is_fake === true || subData?.owner?.role === 'user';
            if (subData) {
                setSubsidiary({
                    ...subData,
                    id: subData.uniq_id ?? subData.id,
                    phone: subData.phone ?? subData.phone_number ?? null,
                    allowTokenPurchase: subData.settings?.authorized_buy_tokens ?? true,
                    is_fake: subData.is_fake ?? false,
                    owner: subData.owner ?? null,
                });
            }       

            // 2) Charger les transactions (toujours) et — uniquement si entreprise — les sous-sous-comptes
            const [txRes, subChildrenRes] = await Promise.all([
                horodatyApi.subsidiaries.getTransactions(companyId, subsidiaryId),
                isFake
                    ? Promise.resolve({ data: [] })
                    : horodatyApi.subsidiaries.list(subsidiaryId).catch(() => ({ data: [] })),
            ]);

            // Map transactions/activities
            const txData = txRes?.data?.data ?? txRes?.data ?? [];
            const mappedActivities = (Array.isArray(txData) ? txData : []).map((tx) => {
                const delta = tx.type === 'credit' ? tx.token_amount : -tx.token_amount;
                return {
                    id: String(tx.uniq_id ?? Math.random()),
                    title: tx.usage || tx.message || t('subsidiary:transaction'),
                    type: usageToType(tx.usage),
                    date: formatDate(tx.created_at),
                    delta,
                    collaborator: tx.user || null,
                };
            });
            setActivities(mappedActivities);

            // Map sub-subsidiaries
            const subChildrenData = subChildrenRes?.data?.data ?? subChildrenRes?.data ?? [];
            setSubSubsidiaries(Array.isArray(subChildrenData) ? subChildrenData : []);
        } catch (error) {
            toast.error(t('subsidiary:impossibleDeChargerLesInformationsDuSousCompte'));
        } finally {
            setIsLoading(false);
        }
    }, [subsidiaryId, companyId, t]);

    useEffect(() => {
        fetchSubsidiary();
    }, [fetchSubsidiary]);

    // Chargement initial + refetch des collaborateurs quand la pagination/recherche change.
    // On attend que `subsidiary` soit chargé : au premier rendu `isIndividual` vaut false
    // (subsidiary === null), un gate sur `isIndividual` seul laisserait donc partir l'appel
    // avant de savoir qu'on est sur un particulier (qui n'a pas d'équipe).
    useEffect(() => {
        if (!subsidiaryId || !companyId) return;
        if (!subsidiary) return;
        if (isIndividual) return;
        refreshCollaborators();
    }, [
        subsidiary?.uniq_id,
        isIndividual,
        collaboratorsPage,
        collaboratorsPageSize,
        collaboratorsSearchApi,
    ]);

    // Handlers
    const refreshSubsidiaryLogo = useCallback(async () => {
        if (!subsidiaryId) return;
        try {
            const res = await horodatyApi.getCompanyLogo(subsidiaryId);
            const blob = res?.data;
            if (!blob || blob.size === 0) return;
            if (blob.type && blob.type.includes('json')) return;
            const url = URL.createObjectURL(blob);
            setSubsidiaryLogoUrl((prev) => {
                if (prev) {
                    try {
                        URL.revokeObjectURL(prev);
                    } catch {}
                }
                return url;
            });
        } catch {
            // no logo
        }
    }, [subsidiaryId]);

    useEffect(() => {
        refreshSubsidiaryLogo();
    }, [refreshSubsidiaryLogo]);

    const handleSubsidiaryUpdate = async (data) => {
        setIsSubmitting(true);
        try {
            await horodatyApi.subsidiaries.update(companyId, subsidiaryId, {
                nom: data.name,
                description: data.description,
                notes: data.notes,
                rue: data.street,
                code_postal: data.postalCode,
                ville: data.city,
                siret: data.siret,
                tva: data.vat,
                phone: data.phone,
                email: data.email,
                billing_email: data.billingEmail || null,
                ...(data.billingId !== undefined && { billing_id: data.billingId || null }),
            });

            if (data.logoBase64 && data.logoFileName) {
                await horodatyApi.updateCompanyLogo(subsidiaryId, {
                    file_name: data.logoFileName,
                    base64: data.logoBase64,
                });
                await refreshSubsidiaryLogo();
            }

            toast.success(t('subsidiary:informationsMisesAJour'));
            setEditOpen(false);
            fetchSubsidiary();
        } catch (error) {
            const message = error?.response?.data?.message || t('subsidiary:erreurLorsDeLaMiseAJour');
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInvite = async (invites) => {
        if (!companyId) return;
        setIsSubmitting(true);
        try {
            const inviteList = Array.isArray(invites) ? invites : [invites];
            await Promise.all(
                inviteList.map((inv) =>
                    horodatyApi.subsidiaries.createUser(companyId, subsidiaryId, {
                        first_name: inv.firstName ?? inv.first_name ?? '',
                        last_name: inv.lastName ?? inv.last_name ?? '',
                        email: inv.email,
                        role: inv.role || undefined,
                    }),
                ),
            );
            toast.success(t('subsidiary:invitationSEnvoyeeS'));
            setInviteOpen(false);
            refreshCollaborators();
        } catch (error) {
            const message = error?.response?.data?.message || t('subsidiary:erreurLorsDeLEnvoiDesInvitations');
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCollaboratorUpdate = async (data) => {
        setIsSubmitting(true);
        try {
            const first_name = data?.first_name || '';
            const last_name = data?.last_name || '';
            const email = data?.email || '';
            const role = data?.role || '';

            await horodatyApi.managerUpdateUser(data.id, {
                first_name: first_name || undefined,
                last_name: last_name || undefined,
                email: email || undefined,
                role: role || undefined,
            });

            toast.success(t('subsidiary:collaborateurMisAJour'));
            setEditCollaboratorOpen(false);
            refreshCollaborators();
        } catch (error) {
            const message = error?.response?.data?.message || t('subsidiary:erreurLorsDeLaMiseAJour');
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCollaboratorDelete = async () => {
        if (!collaboratorToDelete) return;
        setIsSubmitting(true);
        try {
            await horodatyApi.managerArchiveUser(collaboratorToDelete.id);
            toast.success(t('subsidiary:collaborateurArchiveAvecSucces'));
            setDeleteCollaboratorOpen(false);
            setCollaboratorToDelete(null);
            refreshCollaborators();
        } catch (error) {
            toast.error(t('subsidiary:erreurLorsDeLArchivage'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCollaboratorReactivate = async (collaborator) => {
        if (!collaborator) return;
        try {
            await horodatyApi.managerRestoreUser(collaborator.id);
            toast.success(t('subsidiary:collaborateurRestaureAvecSucces'));
            refreshCollaborators();
        } catch (error) {
            toast.error(t('subsidiary:erreurLorsDeLaRestauration'));
        }
    };

    const handleDeleteSubsidiary = async () => {
        setIsSubmitting(true);
        try {
            // TODO-API: L'endpoint DELETE /companies/{id}/childrens/{childId} n'existe pas encore côté API.
            toast.success(t('subsidiary:sousCompteSupprime'));
            setDeleteSubsidiaryOpen(false);
            navigate('/settings');
        } catch (error) {
            toast.error(t('subsidiary:erreurLorsDeLaSuppression'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendTokens = async () => {
        const amount = parseInt(tokenAmount, 10);
        if (!amount || amount <= 0) {
            toast.error(t('subsidiary:veuillezEntrerUnNombreValide'));
            return;
        }
        if (amount > (parentBalance ?? 0)) {
            toast.error(t('subsidiary:soldeInsuffisant'));
            return;
        }
        setIsSendingTokens(true);
        try {
            await horodatyApi.subsidiaries.transferCredits(companyId, subsidiaryId, amount);
            setTokenAmount('');
            toast.success(t('subsidiary:tokensSentTo', { count: amount, name: subsidiary?.name }));
            // Mettre à jour le solde du sous-compte localement
            setSubsidiary((prev) => {
                if (isIndividual) {
                    return { ...prev, owner: { ...prev.owner, balance: (prev?.owner?.balance ?? 0) + amount } };
                }
                return { ...prev, balance: (prev?.balance ?? 0) + amount };
            });
            // Rafraîchir le solde parent + les transactions (sans recharger toute la page)
            const [, txRes] = await Promise.all([
                refreshBalance?.(),
                horodatyApi.subsidiaries.getTransactions(companyId, subsidiaryId).catch(() => null),
            ]);
            if (txRes) {
                const txData = txRes?.data?.data ?? txRes?.data ?? [];
                setActivities(
                    (Array.isArray(txData) ? txData : []).map((tx) => {
                        const delta = tx.type === 'credit' ? tx.token_amount : -tx.token_amount;
                        return {
                            id: String(tx.uniq_id ?? Math.random()),
                            title: tx.usage || tx.message || t('subsidiary:transaction'),
                            type: usageToType(tx.usage),
                            date: formatDate(tx.created_at),
                            delta,
                            collaborator: tx.user || null,
                        };
                    }),
                );
            }
        } catch (error) {
            const message = error?.response?.data?.message || t('subsidiary:erreurLorsDeLEnvoiDesJetons');
            toast.error(message);
        } finally {
            setIsSendingTokens(false);
        }
    };

    const handleOwnerUpdate = async (data) => {
        if (!subsidiary?.owner) return;
        setIsSubmitting(true);
        try {
            await horodatyApi.subsidiaries.updateOwner(companyId, subsidiaryId, {
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
            });
            setSubsidiary((prev) => ({
                ...prev,
                owner: {
                    ...prev.owner,
                    first_name: data.first_name,
                    last_name: data.last_name,
                    email: data.email,
                },
            }));
            toast.success(t('subsidiary:informationsMisesAJour'));
            setOwnerEditOpen(false);
        } catch (error) {
            const message = error?.response?.data?.message || t('subsidiary:erreurLorsDeLaMiseAJour');
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRightsUpdate = async (data) => {
        if (!companyId) return;
        setIsSubmitting(true);
        try {
            await horodatyApi.subsidiaries.updateBuyTokensAuth(
                companyId,
                subsidiaryId,
                data.allowTokenPurchase ?? data.authorized_buy_tokens ?? false,
            );
            setSubsidiary((prev) => ({
                ...prev,
                allowTokenPurchase: data.allowTokenPurchase ?? data.authorized_buy_tokens,
            }));
            toast.success(t('subsidiary:droitsMisAJour'));
            setRightsOpen(false);
        } catch (error) {
            const message = error?.response?.data?.message || t('subsidiary:erreurLorsDeLaMiseAJourDes');
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('subsidiary:chargement')}</div>
            </div>
        );
    }

    if (!subsidiary) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="text-muted-foreground">{t('subsidiary:sousCompteIntrouvable')}</div>
                <Button variant="outline" color="neutral" onClick={() => navigate(backUrl)}>
                    {t('subsidiary:retour')}
                </Button>
            </div>
        );
    }

    const activityHistoryBlock = (
        <div className="bg-card rounded-2xl overflow-hidden border border-border">
            {/* Header */}
            <div className="bg-card w-full p-5 flex items-center justify-between border-b border-border">
                <div className="flex items-center gap-3">
                    <div className="bg-muted rounded-full h-10 w-10 flex items-center justify-center">
                        <ClipboardList className="h-5 w-5 text-foreground" />
                    </div>
                    <h2 className="text-[14px] text-foreground">{t('subsidiary:historiqueDActions')}</h2>
                </div>
                <Button
                    variant="plain"
                    color="neutral"
                    size="auto"
                    className="w-9 h-9 bg-card rounded-full flex items-center justify-center hover:bg-border transition-colors"
                    onClick={() => setActivityFilterOpen(true)}
                >
                    <Filter className="h-5 w-5 text-foreground" />
                </Button>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block">
                <DataTable
                    columns={activityColumns}
                    data={activities}
                    emptyMessage={t('subsidiary:aucuneActivite')}
                    showPagination={true}
                    pageSize={10}
                />
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden p-3">
                <div className="space-y-2">
                    {activities.map((item) => (
                        <div key={item.id} className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    item.type === 'procedure_filled'
                                        ? 'bg-badge-filled/20'
                                        : item.type === 'procedure_created'
                                          ? 'bg-badge-created/20'
                                          : item.type === 'photo'
                                            ? 'bg-secondary/20'
                                            : item.type === 'tokens'
                                              ? 'bg-secondary/20'
                                              : item.type === 'invitation'
                                                ? 'bg-badge-invitation/20'
                                                : 'bg-muted'
                                }`}
                            >
                                {item.type === 'procedure_filled' && (
                                    <FileText className="h-5 w-5 text-badge-filled" />
                                )}
                                {item.type === 'procedure_created' && (
                                    <ClipboardList className="h-5 w-5 text-badge-created" />
                                )}
                                {item.type === 'photo' && <Camera className="h-5 w-5 text-secondary" />}
                                {item.type === 'tokens' && <TrendingUp className="h-5 w-5 text-secondary" />}
                                {item.type === 'invitation' && (
                                    <UserCheck className="h-5 w-5 text-badge-invitation" />
                                )}
                                {!item.type && <ClipboardList className="h-5 w-5 text-muted-foreground" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[14px] text-foreground">{item.title}</p>
                                <p className="text-[12px] text-muted-foreground">
                                    {item.date}
                                    {item.collaborator ? ` · ${item.collaborator}` : ''}
                                </p>
                            </div>
                            {item.delta !== 0 && item.delta !== undefined && (
                                <span
                                    className={`text-[15px] flex-shrink-0 ${
                                        item.delta > 0 ? 'text-secondary' : 'text-destructive'
                                    }`}
                                >
                                    {item.delta > 0 ? `+${item.delta}` : item.delta}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* Mobile Header */}
            <MobileHeaderNavigation
                title={isIndividual ? (ownerFullName || (subsidiary?.name ?? t('subsidiary:particulier'))) : (subsidiary?.name ?? t('subsidiary:sousCompte'))}
                onBack={() => navigate(backUrl)}
                menuItems={[
                    ...(!isIndividual && canOnDescendants('UPDATE_SUBSIDIARY_PERMISSIONS')
                        ? [
                              {
                                  label: t('subsidiary:gererLesDroits'),
                                  onClick: () => setRightsOpen(true),
                              },
                          ]
                        : []),
                    .../* canOnDescendants('DELETE_SUBSIDIARIES') */ (isOwnerOrAdmin()
                        ? [
                              {
                                  label: t('subsidiary:supprimer'),
                                  onClick: () => setDeleteSubsidiaryOpen(true),
                                  danger: true,
                              },
                          ]
                        : []),
                ]}
            />

            <div className="min-h-full pb-8">
                {/* Desktop Page Header */}
                <div className="desktop-only hidden md:block bg-background border-b border-border">
                    <div className="px-5 py-4 flex items-center justify-between">
                        <BackButton onClick={() => navigate(backUrl)} className="w-9 h-9 bg-muted hover:bg-border" />
                        <h1 className="text-[16px] text-foreground font-medium">{isIndividual ? (ownerFullName || (subsidiary?.name ?? t('subsidiary:particulier'))) : (subsidiary?.name ?? t('subsidiary:sousCompte'))}</h1>
                        <div className="relative">
                            <Button
                                variant="plain"
                                color="neutral"
                                size="auto"
                                onClick={() => setMenuOpen(!menuOpen)}
                                className="w-9 h-9 bg-muted rounded-full flex items-center justify-center hover:bg-border transition-colors"
                            >
                                <EllipsisVertical className="h-5 w-5 text-foreground" />
                            </Button>
                            {menuOpen && (
                                <>
                                    <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                                    <div className="absolute right-0 top-11 bg-card rounded-xl shadow-lg border border-border py-2 w-[200px] z-50">
                                        {!isIndividual && canOnDescendants('UPDATE_SUBSIDIARY_PERMISSIONS') && (
                                            <Button
                                                variant="plain"
                                                color="neutral"
                                                onClick={() => {
                                                    setMenuOpen(false);
                                                    setRightsOpen(true);
                                                }}
                                                className="w-full h-auto px-4 py-3 text-left justify-start rounded-none hover:bg-muted transition-colors"
                                            >
                                                <span className="text-[14px] text-foreground">{t('subsidiary:gererLesDroits')}</span>
                                            </Button>
                                        )}
                                        {
                                            /* canOnDescendants('DELETE_SUBSIDIARIES') */ isOwnerOrAdmin() && (
                                                <Button
                                                    variant="plain"
                                                    color="neutral"
                                                    onClick={() => {
                                                        setMenuOpen(false);
                                                        setDeleteSubsidiaryOpen(true);
                                                    }}
                                                    className="w-full h-auto px-4 py-3 text-left justify-start rounded-none hover:bg-muted transition-colors"
                                                >
                                                    <span className="text-[14px] text-destructive">{t('subsidiary:supprimer')}</span>
                                                </Button>
                                            )
                                        }
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-8 py-6 space-y-6">
                    {/* Top: Info + Collaborators */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div ref={companyInfoRef} className="space-y-6">
                            {/* Subsidiary Info */}
                            {!isIndividual && (
                            <div className="flex flex-col bg-card rounded-2xl overflow-hidden border border-border">
                                {/* Header with gradient */}
                                <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 w-full p-[25px] flex flex-col items-center border-b border-border relative">
                                    {canOnDescendants('UPDATE_SUBSIDIARIES') && (
                                        <Button
                                            variant="solid"
                                            color="dark"
                                            size="xmd"
                                            onClick={() => setEditOpen(true)}
                                            className="absolute top-4 right-4 text-[13px]"
                                        >
                                            {t('subsidiary:modifier')}
                                        </Button>
                                    )}
                                    <div
                                        className={`h-[60px] w-[60px] flex items-center justify-center mb-3 overflow-hidden ${subsidiaryLogoUrl ? '' : 'bg-secondary rounded-full'}`}
                                    >
                                        {subsidiaryLogoUrl ? (
                                            <img
                                                src={subsidiaryLogoUrl}
                                                alt={t('subsidiary:logo')}
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <Building2 className="h-7 w-7 text-secondary-foreground" />
                                        )}
                                    </div>
                                    <h2 className="text-[18px] text-foreground mb-[5px] text-center">
                                        {subsidiary?.name ?? t('subsidiary:sousCompte')}
                                    </h2>
                                    <p className="text-[14px] text-muted-foreground">
                                        {t('subsidiary:siret')} {subsidiary?.siret ?? '—'}
                                    </p>
                                </div>

                                {/* Details */}
                                <div className="p-[21px] space-y-4 flex-1">
                                    <div>
                                        <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                            {t('subsidiary:nTvaIntracommunautaire')}
                                        </label>
                                        <p className="text-[13px] text-foreground">{subsidiary?.vat ?? '—'}</p>
                                    </div>
                                    <div>
                                        <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                            {t('subsidiary:description')}
                                        </label>
                                        <p className="text-[13px] text-foreground">{subsidiary?.description ?? '—'}</p>
                                    </div>
                                    <div>
                                        <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                            {t('subsidiary:notes')}
                                        </label>
                                        <p className="text-[13px] text-foreground">{subsidiary?.notes ?? '—'}</p>
                                    </div>
                                    <div>
                                        <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                            {t('subsidiary:adresse')}
                                        </label>
                                        <p className="text-[13px] text-foreground">
                                            {[subsidiary?.street, subsidiary?.cp, subsidiary?.city]
                                                .filter(Boolean)
                                                .join(', ') || '—'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                            {t('subsidiary:emailDeContact')}
                                        </label>
                                        <p className="text-[13px] text-foreground">{subsidiary?.email ?? '—'}</p>
                                    </div>
                                    <div>
                                        <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                            {t('subsidiary:emailDeFacturation')}
                                        </label>
                                        <p className="text-[13px] text-foreground">
                                            {subsidiary?.billing_email ?? '—'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                            {t('subsidiary:telephone')}
                                        </label>
                                        <p className="text-[13px] text-foreground">{subsidiary?.phone ?? '—'}</p>
                                    </div>
                                    <div>
                                        <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                            {t('subsidiary:idDeFacturation')}
                                        </label>
                                        <p className="text-[13px] text-foreground">{subsidiary?.billing_id ?? '—'}</p>
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Individual Info */}
                            {isIndividual && (
                                <div className="flex flex-col bg-card rounded-2xl overflow-hidden border border-border">
                                    {/* Header with gradient */}
                                    <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 w-full p-[25px] flex flex-col items-center border-b border-border relative">
                                        {canOnDescendants('UPDATE_SUBSIDIARIES') && (
                                            <Button
                                                variant="solid"
                                                color="dark"
                                                size="xmd"
                                                onClick={() => setOwnerEditOpen(true)}
                                                className="absolute top-4 right-4 text-[13px]"
                                            >
                                                {t('subsidiary:modifier')}
                                            </Button>
                                        )}
                                        <div className="h-[60px] w-[60px] flex items-center justify-center mb-3 bg-secondary rounded-full">
                                            <User className="h-7 w-7 text-secondary-foreground" />
                                        </div>
                                        <h2 className="text-[18px] text-foreground mb-[5px] text-center">
                                            {ownerFullName || '—'}
                                        </h2>
                                    </div>
                                    {/* Details */}
                                    <div className="p-[21px] space-y-4 flex-1">
                                        <div>
                                            <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                                {t('subsidiary:prenom')}
                                            </label>
                                            <p className="text-[13px] text-foreground">{subsidiary?.owner?.first_name ?? '—'}</p>
                                        </div>
                                        <div>
                                            <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                                {t('subsidiary:nom2')}
                                            </label>
                                            <p className="text-[13px] text-foreground">{subsidiary?.owner?.last_name ?? '—'}</p>
                                        </div>
                                        <div>
                                            <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                                {t('subsidiary:email')}
                                            </label>
                                            <p className="text-[13px] text-foreground">{subsidiary?.owner?.email ?? '—'}</p>
                                        </div>
                                        <div>
                                            <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                                {t('subsidiary:telephone')}
                                            </label>
                                            <p className="text-[13px] text-foreground">{subsidiary?.owner?.phone ?? subsidiary?.phone ?? '—'}</p>
                                        </div>
                                        <div>
                                            <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                                {t('subsidiary:role')}
                                            </label>
                                            {subsidiary?.owner?.role ? (
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs ${getRoleBadgeClass(subsidiary.owner.role)}`}
                                                >
                                                    {getRoleLabel(subsidiary.owner.role)}
                                                </span>
                                            ) : (
                                                <p className="text-[13px] text-foreground">—</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="text-[13px] text-muted-foreground mb-[6px] block">
                                                {t('subsidiary:statut')}
                                            </label>
                                            {subsidiary?.owner?.status ? (
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs ${getUserStatusBadgeClass(subsidiary.owner.status)}`}
                                                >
                                                    {getUserStatusLabel(subsidiary.owner.status)}
                                                </span>
                                            ) : (
                                                <p className="text-[13px] text-foreground">—</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Token Balance */}
                            {canOnDescendants('VIEW_COMPANY_TOKENS') && (
                                <div className="bg-dark rounded-2xl p-4">
                                    <h3 className="text-[14px] text-dark-foreground font-medium mb-3">
                                        {t('subsidiary:soldeDeJetons')}
                                    </h3>
                                    <div className="flex gap-3 mb-3">
                                        <div className="flex-1 flex flex-col items-center justify-center p-3 bg-white/10 rounded-lg">
                                            <span className="text-[12px] text-dark-foreground/70 mb-1 text-center">
                                                {t('subsidiary:votreSolde')}
                                            </span>
                                            <span className="text-[16px] text-secondary font-semibold">
                                                {parentBalance ?? 0}
                                            </span>
                                        </div>
                                        <div className="flex-1 flex flex-col items-center justify-center p-3 bg-white/10 rounded-lg">
                                            <span className="text-[12px] text-dark-foreground/70 mb-1 text-center">
                                                {isIndividual ? (ownerFullName || t('subsidiary:particulier')) : (subsidiary?.name ?? t('subsidiary:sousCompte'))}
                                            </span>
                                            <span className="text-[16px] text-dark-foreground font-semibold">
                                                {(isIndividual ? subsidiary?.owner?.balance : subsidiary?.balance) ?? 0}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="number"
                                            placeholder={t('subsidiary:nombre')}
                                            value={tokenAmount}
                                            onChange={(e) => setTokenAmount(e.target.value)}
                                            min="1"
                                            max={parentBalance ?? 0}
                                            step="1"
                                            className="flex-1 bg-white/10 rounded-lg px-3 py-2.5 text-[13px] text-dark-foreground placeholder:text-background/50 outline-none focus:ring-2 focus:ring-secondary border border-background/20"
                                        />
                                        <Button
                                            variant="solid"
                                            color="secondary"
                                            type="button"
                                            onClick={handleSendTokens}
                                            disabled={
                                                isSendingTokens ||
                                                !tokenAmount ||
                                                parseInt(tokenAmount, 10) <= 0 ||
                                                parseInt(tokenAmount, 10) > (parentBalance ?? 0)
                                            }
                                            className="rounded-lg py-2.5 px-4 text-[13px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                                        >
                                            {isSendingTokens ? t('subsidiary:envoi2') : t('subsidiary:envoyer')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Collaborators */}
                        {!isIndividual && canOnDescendants('LIST_USERS') && (
                            <CollaboratorsDataTable
                                collaborators={collaborators}
                                height={collaboratorsHeight}
                                onInvite={
                                    canOnDescendants('CREATE_COMPANY_MEMBERS') ? () => setInviteOpen(true) : undefined
                                }
                                // TODO: créer la page de permissions pour les sous-comptes
                                // canEditPermissions={hasEditablePermissions('DESCENDANTS')}
                                onEdit={
                                    canOnDescendants('UPDATE_COMPANY_MEMBERS')
                                        ? (c) => {
                                              setSelectedCollaborator(c);
                                              setEditCollaboratorOpen(true);
                                          }
                                        : undefined
                                }
                                onDelete={
                                    canOnDescendants('ARCHIVE_COMPANY_MEMBERS')
                                        ? (c) => {
                                              setCollaboratorToDelete(c);
                                              setDeleteCollaboratorOpen(true);
                                          }
                                        : undefined
                                }
                                onReactivate={
                                    canOnDescendants('ARCHIVE_COMPANY_MEMBERS')
                                        ? (c) => handleCollaboratorReactivate(c)
                                        : undefined
                                }
                                manualPagination={true}
                                pageCount={collaboratorsPageCount}
                                currentPage={collaboratorsPage}
                                onPageChange={setCollaboratorsPage}
                                onPageSizeChange={(size) => {
                                    setCollaboratorsPageSize(size);
                                    setCollaboratorsPage(1);
                                }}
                                pageSize={collaboratorsPageSize}
                                totalCount={collaboratorsTotal}
                                isLoading={isLoadingCollaborators}
                                searchValue={collaboratorSearch}
                                onSearchChange={handleCollaboratorSearchChange}
                            />
                        )}

                        {/* Activity history (right column for individuals) */}
                        {isIndividual && activityHistoryBlock}
                    </div>

                    {/* Sub-subsidiaries list */}
                    {!isIndividual && canOnDescendants('UPDATE_SUBSIDIARIES') && (
                        <div className="bg-card rounded-2xl overflow-hidden border border-border">
                            <div className="w-full p-5 flex items-center justify-between border-b border-border">
                                <div className="flex items-center gap-3">
                                    <div className="bg-muted rounded-full h-10 w-10 flex items-center justify-center">
                                        <Building2 className="h-5 w-5 text-foreground" />
                                    </div>
                                    <h2 className="text-[14px] text-foreground">{t('subsidiary:sousComptes')}</h2>
                                </div>
                                <Button
                                    variant="plain"
                                    size="icon"
                                    className="w-9 h-9 bg-brand rounded-full flex items-center justify-center hover:bg-brand/90 transition-colors"
                                    onClick={() => setSubsidiaryCreateOpen(true)}
                                >
                                    <Plus className="h-5 w-5 text-foreground" />
                                </Button>
                            </div>

                            {isLoadingSubsidiaries ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : subSubsidiaries.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-3 gap-3">
                                    <p className="text-[14px] text-muted-foreground">{t('subsidiary:aucunSousCompte')}</p>
                                </div>
                            ) : (
                                <>
                                    <div className="hidden md:block">
                                        <DataTable
                                            columns={subsidiaryColumns}
                                            data={subSubsidiaries}
                                            onRowClick={(sub) =>
                                                navigate(`/settings/subsidiary/${sub.uniq_id ?? sub.id}`)
                                            }
                                            emptyMessage={t('subsidiary:aucunSousCompte')}
                                            emptyIcon={<Building2 className="w-12 h-12 text-muted-foreground/50" />}
                                            showPagination={false}
                                        />
                                    </div>
                                    <div className="md:hidden p-3">
                                        <div className="space-y-2">
                                            {subSubsidiaries.map((sub) => (
                                                <Button
                                                    key={sub.uniq_id ?? sub.id}
                                                    variant="plain"
                                                    onClick={() =>
                                                        navigate(`/settings/subsidiary/${sub.uniq_id ?? sub.id}`)
                                                    }
                                                    className="w-full h-auto p-4 bg-muted rounded-xl hover:bg-border transition-colors text-left flex-col items-start"
                                                >
                                                    <div className="flex items-start justify-between mb-2 w-full">
                                                        <h3 className="text-[15px] text-foreground font-medium">
                                                            {sub.name}
                                                        </h3>
                                                        <div className="flex items-center gap-1.5 bg-brand/10 px-2 py-1 rounded-md">
                                                            <Coins className="h-3 w-3 text-brand" />
                                                            <span className="text-[13px] text-foreground font-medium">
                                                                {sub.balance ?? 0}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <p className="text-[13px] text-muted-foreground mb-1">
                                                        {sub.email}
                                                    </p>
                                                    <p className="text-[13px] text-muted-foreground">
                                                        {sub.city ?? '—'}
                                                    </p>
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Activity history (full width for companies) */}
                    {!isIndividual && activityHistoryBlock}
                </div>
            </div>

            {/* Sheets */}
            <SheetSubsidiaryEdit
                open={editOpen}
                onOpenChange={setEditOpen}
                subsidiary={subsidiary}
                subsidiaryLogoUrl={subsidiaryLogoUrl}
                onSubmit={handleSubsidiaryUpdate}
                isSubmitting={isSubmitting}
            />

            <SheetSubsidiaryInvite
                open={inviteOpen}
                onOpenChange={setInviteOpen}
                subsidiary={subsidiary}
                onSubmit={handleInvite}
                isSubmitting={isSubmitting}
                hasOwner={collaborators.some(
                    (c) => (typeof c.role === 'string' ? c.role : c.role?.name)?.toLowerCase() === 'owner',
                )}
                isSuperAdmin={authUser?.role === 'admin'}
            />

            <SheetCollaboratorEdit
                open={editCollaboratorOpen}
                onOpenChange={setEditCollaboratorOpen}
                collaborator={selectedCollaborator}
                onSubmit={handleCollaboratorUpdate}
                isSubmitting={isSubmitting}
            />

            <SheetOwnerEdit
                open={ownerEditOpen}
                onOpenChange={setOwnerEditOpen}
                owner={subsidiary?.owner}
                onSubmit={handleOwnerUpdate}
                isSubmitting={isSubmitting}
            />

            <SheetActivityFilter
                open={activityFilterOpen}
                onClose={() => setActivityFilterOpen(false)}
                collaborators={collaborators}
                currentFilter={activityFilter}
                resetKey={activityFilterResetKey}
                onSubmit={(filter) => {
                    setActivityFilter(filter);
                    setActivityFilterOpen(false);
                }}
            />

            <SheetSubsidiaryRights
                open={rightsOpen}
                onOpenChange={setRightsOpen}
                subsidiary={subsidiary}
                onSubmit={handleRightsUpdate}
                isSubmitting={isSubmitting}
            />

            <AlertDialogDeleteCollaborator
                open={deleteCollaboratorOpen}
                onOpenChange={setDeleteCollaboratorOpen}
                collaborator={collaboratorToDelete}
                isSubmitting={isSubmitting}
                onConfirm={handleCollaboratorDelete}
            />

            <AlertDialogDeleteSubsidiary
                open={deleteSubsidiaryOpen}
                onOpenChange={setDeleteSubsidiaryOpen}
                subsidiary={subsidiary}
                onConfirm={handleDeleteSubsidiary}
                isSubmitting={isSubmitting}
            />

            <SheetSubsidiaryCreate
                open={subsidiaryCreateOpen}
                onClose={() => setSubsidiaryCreateOpen(false)}
                resetKey={subsidiaryCreateOpen}
                isSubmitting={isSubmittingSheet}
                onSubmit={async (data) => {
                    setIsSubmittingSheet(true);
                    try {
                        await horodatyApi.subsidiaries.create(subsidiaryId, {
                            company: {
                                name: data.name,
                                street: data.street || '',
                                cp: data.postalCode || '',
                                city: data.city || '',
                                siret: data.siret,
                                tva: data.vat || null,
                                email: data.email,
                                description: data.description || null,
                                notes: data.notes || null,
                                phone: data.phone || null,
                                authorized_buy_tokens: data.allowTokenPurchase ?? true,
                                ...(data.billingId !== undefined && { billing_id: data.billingId || null }),
                            },
                            owner: data.managerEmail
                                ? {
                                      firstname: data.managerFirstName,
                                      lastname: data.managerLastName,
                                      email: data.managerEmail,
                                  }
                                : undefined,
                        });
                        // Refresh sub-subsidiaries
                        const subChildrenRes = await horodatyApi.subsidiaries
                            .list(subsidiaryId)
                            .catch(() => ({ data: [] }));
                        const subChildrenData = subChildrenRes?.data?.data ?? subChildrenRes?.data ?? [];
                        setSubSubsidiaries(Array.isArray(subChildrenData) ? subChildrenData : []);
                        setSubsidiaryCreateOpen(false);
                        toast.success(t('subsidiary:sousCompteAjoute'));
                    } catch (error) {
                        const message = error?.response?.data?.message || t('subsidiary:impossibleDAjouterLeSousCompte');
                        toast.error(message);
                    } finally {
                        setIsSubmittingSheet(false);
                    }
                }}
            />
        </>
    );
}
